/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Menu,
  message,
  Modal,
  Switch,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps,
  FilterValueProps,
  mappingFilterFields,
  mappingParamsFilter,
  mappingQueryParamsFilter,
} from 'common/components/PageTable/filter';
import { deleteUseCaseService, getListKnowledgeUseCasesService, updateUseCaseStatusService } from 'common/services/extends/knowledgeBase';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';

export interface KnowledgeBaseItem {
  useCaseData: {
    id: number;
    code: string;
    name: string;
    description: string;
    status: number;
    ttlFile: number;
    ttlSize: number;
    createdAt: string;
    updatedAt: string;
  };
  updater?: {
    email: string;
    name: string;
    avatar: string | null;
    bgAvatar: string | null;
  };
  creator?: {
    email: string;
    name: string;
    avatar: string | null;
    bgAvatar: string | null;
  };
}

/* =======================
   Component
======================= */

const KnowledgeBase: React.FC<ActiveRoles> = ({ roleCreate, roleUpdate, roleDelete }) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /* Store */
  const {
    system: { defaultPageSize, advancedFilter },
  } = useAppSelector((state) => state);

  /* Search params */
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const obj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete obj.page;
    return obj;
  }, [searchParams]);

  /* States */
  const [currentPage, setCurrentPage] = useState(1);
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]
  >(mappingQueryParamsFilter(params));

  /* Filters */
  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('knowledgeBase', advancedFilter),
    [advancedFilter]
  );

  const queryKey = [
    'knowledgeBase',
    currentPage,
    currentView,
    keyword,
    selectedFilterList,
  ];

  const { data, isLoading } = useQuery(queryKey, async () => {
    const res = await getListKnowledgeUseCasesService({
      page: currentPage,
      limit: currentView,
      keyword,
      ...returnParamsFilter,
    });
    return res;
  });

  const { mutate: deleteMutate } = useMutation(
    (ids: number[]) => deleteUseCaseService({ ids }),
    {
      onSuccess: () => {
        message.success('Deleted successfully');
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error('Failed to delete');
      },
    }
  );

  const { mutate: updateStatusMutate, isLoading: updateStatusLoading } = useMutation(
    (payload: { id: number; status: number }) => (
      updateUseCaseStatusService(payload.id, payload.status)
    ),
    {
      onSuccess: () => {
        message.success('Status updated successfully');
        queryClient.invalidateQueries('knowledgeBase');
      },
      onError: () => {
        message.error('Failed to update status');
      },
    }
  );

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({ page: page.toString() }, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    setSelectedFilterList((prev) => prev.filter(
      (item) => !(item.key === key && item.index === index)
    ));
  };

  const handleFilter = (dataFilter: FilterValueProps) => {
    const counter = selectedFilterList.filter(
      (item) => item.field === dataFilter.field && item.filter === dataFilter.filter
    ).length;

    setSelectedFilterList([
      ...selectedFilterList,
      { ...dataFilter, index: counter },
    ]);
  };

  const columns: ColumnsType<KnowledgeBaseItem> = useMemo(
    () => [
      {
        title: 'Use Case Name',
        dataIndex: ['useCaseData', 'name'],
        key: 'name',
        sorter: (a, b) => a.useCaseData.name.localeCompare(b.useCaseData.name),
        render: (text, record) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography.Link
              strong
              onClick={() => navigate(`${ROUTE_PATHS.KNOWLEDGE_BASE_DETAIL}?id=${record.useCaseData?.id}&view=true`)}
              style={{ fontSize: '15px', color: 'black' }}
            >
              {text}
            </Typography.Link>
            <Typography.Text type="secondary" ellipsis style={{ maxWidth: 350, fontSize: '13px' }}>
              {record.useCaseData.description || 'No description provided.'}
            </Typography.Text>
          </div>
        ),
        width: 350,
      },
      {
        title: 'Status',
        dataIndex: ['useCaseData', 'status'],
        key: 'status',
        render: (status, record) => (
          <Switch
            checked={status === 2}
            loading={updateStatusLoading}
            onChange={(checked) => {
              updateStatusMutate({
                id: record.useCaseData.id,
                status: checked ? 2 : 1,
              });
            }}
          />
        ),
        width: 120,
      },
      {
        title: 'Total Files',
        dataIndex: ['useCaseData', 'ttlFile'],
        key: 'totalFiles',
        align: 'center',
        render: (value) => (
          <Typography.Text>
            {value || 0}
            {' '}
            files
          </Typography.Text>
        ),
        width: 120,
      },

      {
        title: 'Total Size',
        dataIndex: ['useCaseData', 'ttlSize'],
        key: 'totalSize',
        align: 'center',
        render: (value) => (
          <Typography.Text>
            {(value || 0).toFixed(2)}
            {' '}
            MB
          </Typography.Text>
        ),
        width: 120,
      },
      {
        title: 'Last Updated',
        key: 'updatedAt',
        render: (_, record) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography.Text style={{ fontSize: '14px', fontWeight: 500 }}>
              {formatDateTime(record.useCaseData?.updatedAt)}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              by
              {' '}
              {record.updater?.name || '-'}
            </Typography.Text>
          </div>
        ),
        width: 250,
      },
      {
        title: '',
        key: 'action',
        width: 60,
        align: 'center',
        render: (_, record) => {
          const menu = (
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(`${ROUTE_PATHS.KNOWLEDGE_BASE_DETAIL}?id=${record.useCaseData?.id}&view=true`)}
              >
                View
              </Menu.Item>
              {roleUpdate && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`${ROUTE_PATHS.KNOWLEDGE_BASE_DETAIL}?id=${record.useCaseData?.id}`)}
                >
                  Edit
                </Menu.Item>
              )}
              {roleDelete && (
                <Menu.Item
                  key="delete"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Confirm delete this use case?',
                      onOk: () => {
                        deleteMutate([record.useCaseData.id]);
                      },
                    });
                  }}
                >
                  Delete
                </Menu.Item>
              )}
            </Menu>
          );
          return (
            <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [navigate, deleteMutate, roleUpdate, roleDelete, updateStatusLoading, updateStatusMutate]
  );

  return (
    <>
      <HeaderPage
        fixed
        title="Knowledge Base Use Cases"
        rightHeader={(
          roleCreate && (
            <Button
              type="primary"
              onClick={() => navigate(ROUTE_PATHS.KNOWLEDGE_BASE_DETAIL)}
            >
              <PlusOutlined />
              Create Use Case
            </Button>
          )
        )}
      />

      <div className="t-mainlayout_wrapper">
        <PageTable
          isLoading={isLoading || updateStatusLoading}
          handleSearch={setKeyword}
          tableProps={{
            initShowColumns: [
              'name',
              'description',
              'status',
              'totalSize',
              'totalFiles',
              'createdAt',
              'updatedAt',
              'action',
            ],
            columns,
            pageData: data?.data || [],
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: data?.meta.total || 0,
            noBaseCol: true,
          }}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
        />
      </div>
    </>
  );
};

export default KnowledgeBase;
